import { wavelengthToHex } from "@/lib/spectrum";

export interface SpectrumLine {
  nm: number;
  color: string;
  from: number; // n_high
  to: number; // n_low
  count: number; // hits at this wavelength
}

interface Props {
  lines: SpectrumLine[];
  onClear?: () => void;
  exportName?: string;
}

const MIN_NM = 380;
const MAX_NM = 780;

/** Build a CSS gradient that approximates the visible spectrum. */
function buildGradient(): string {
  const stops: string[] = [];
  for (let nm = MIN_NM; nm <= MAX_NM; nm += 20) {
    const pct = ((nm - MIN_NM) / (MAX_NM - MIN_NM)) * 100;
    stops.push(`${wavelengthToHex(nm)} ${pct.toFixed(1)}%`);
  }
  return `linear-gradient(to right, ${stops.join(", ")})`;
}

const SPECTRUM_GRADIENT = buildGradient();
const TICKS = [400, 450, 500, 550, 600, 650, 700, 750];

export default function EmissionSpectrum({ lines, onClear, exportName }: Props) {
  const handleExport = () => {
    const header = "wavelength_nm,from_n,to_n,count,color\n";
    const rows = lines
      .slice()
      .sort((a, b) => a.nm - b.nm)
      .map((l) => `${l.nm.toFixed(2)},${l.from},${l.to},${l.count},${l.color}`)
      .join("\n");
    const blob = new Blob([header + rows], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const safe = (exportName ?? "spectrum").replace(/[^a-z0-9_-]+/gi, "_");
    a.href = url;
    a.download = `${safe}_emission_spectrum.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <section
      aria-label="Emission spectrum"
      className="rounded-lg border border-border bg-tile-bg p-3"
    >
      <header className="flex items-center justify-between mb-2 gap-2">
        <div className="min-w-0">
          <h2 className="text-sm font-semibold text-foreground">Emission spectrum</h2>
          <p className="text-[11px] text-muted-foreground">
            Each excitation records a line on the visible spectrum (380–780 nm).
          </p>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          {lines.length > 0 && (
            <button
              type="button"
              onClick={handleExport}
              className="text-[11px] text-muted-foreground hover:text-foreground underline-offset-4 hover:underline"
              aria-label="Export emission spectrum as CSV"
            >
              Export CSV
            </button>
          )}
          {onClear && lines.length > 0 && (
            <button
              type="button"
              onClick={onClear}
              className="text-[11px] text-muted-foreground hover:text-foreground underline-offset-4 hover:underline"
            >
              Clear ({lines.length})
            </button>
          )}
        </div>
      </header>

      {/* Spectrum strip */}
      <div className="relative h-12 rounded-md overflow-hidden border border-border bg-black">
        {/* Faded rainbow background for orientation */}
        <div
          aria-hidden="true"
          className="absolute inset-0 opacity-25"
          style={{ background: SPECTRUM_GRADIENT }}
        />

        {/* Recorded lines */}
        {lines.map((line, i) => {
          const clamped = Math.max(MIN_NM, Math.min(MAX_NM, line.nm));
          const pct = ((clamped - MIN_NM) / (MAX_NM - MIN_NM)) * 100;
          const intensity = Math.min(1, 0.4 + line.count * 0.15);
          const inVisible = line.nm >= MIN_NM && line.nm <= MAX_NM;
          return (
            <div
              key={i}
              role="img"
              aria-label={`Line at ${Math.round(line.nm)} nanometres, transition n=${line.from} to n=${line.to}, ${line.count} hit${line.count === 1 ? "" : "s"}`}
              title={`${Math.round(line.nm)} nm · n=${line.from} → n=${line.to}${inVisible ? "" : " (outside visible)"}`}
              className="absolute top-0 bottom-0"
              style={{
                left: `${pct}%`,
                width: 2,
                marginLeft: -1,
                background: line.color,
                boxShadow: `0 0 6px ${line.color}, 0 0 14px ${line.color}`,
                opacity: intensity,
              }}
            />
          );
        })}

        {/* Outside-visible markers (UV/IR) on the edges */}
        {lines.some((l) => l.nm < MIN_NM) && (
          <div
            aria-hidden="true"
            className="absolute left-0 top-0 bottom-0 w-1 bg-primary/70"
            title="Ultraviolet emissions present"
          />
        )}
        {lines.some((l) => l.nm > MAX_NM) && (
          <div
            aria-hidden="true"
            className="absolute right-0 top-0 bottom-0 w-1 bg-destructive/70"
            title="Infrared emissions present"
          />
        )}
      </div>

      {/* Tick marks */}
      <div className="relative h-4 mt-1 text-[10px] text-muted-foreground font-mono">
        {TICKS.map((t) => {
          const pct = ((t - MIN_NM) / (MAX_NM - MIN_NM)) * 100;
          return (
            <span
              key={t}
              className="absolute -translate-x-1/2 tabular-nums"
              style={{ left: `${pct}%` }}
            >
              {t}
            </span>
          );
        })}
      </div>

      {/* Recent transitions list */}
      {lines.length > 0 && (
        <ul className="mt-3 flex flex-wrap gap-1.5">
          {lines
            .slice()
            .sort((a, b) => a.nm - b.nm)
            .map((line, i) => (
              <li
                key={i}
                className="flex items-center gap-1.5 px-2 py-0.5 rounded-full border border-border bg-background text-[10px] font-mono"
              >
                <span
                  aria-hidden="true"
                  className="inline-block h-2 w-2 rounded-full"
                  style={{ background: line.color, boxShadow: `0 0 4px ${line.color}` }}
                />
                <span>
                  n={line.from}→{line.to} · {Math.round(line.nm)} nm
                  {line.count > 1 && <span className="text-muted-foreground"> ×{line.count}</span>}
                </span>
              </li>
            ))}
        </ul>
      )}
    </section>
  );
}
